// auditpm — Complaints (simple CRUD log). No workflow, no escalation, no automation.
import React, { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { format, parseISO } from 'date-fns'
import { useAuth } from '../lib/AuthContext'
import { toast, failed } from '../lib/toast'

const STATUSES = ['Open', 'Under Review', 'Pending Response', 'Closed']
const YESNO = ['', 'Yes', 'No']

const BLANK = {
  record_type: 'Complaint', complaint_ref: '', complaint_date: '', received_by: '',
  complainant_name: '', client: '', contact_details: '', job_id: '', subject: '', description: '',
  within_scope: '', investigation_notes: '', outcome: '', corrective_required: '', corrective_action: '',
  handled_by: '', reviewed_by: '', independence_note: '',
  status: 'Open', action_taken: '', closed_date: '',
}
const SCOPE_OPTS = ['', 'Yes', 'No', 'Unclear']

// Auto reference: complaints get C{n}. User never enters/edits this.
function computeNextRef(rows) {
  const re = /^C(\d+)$/
  let max = 0
  for (const r of (rows || [])) {
    const m = re.exec(r.complaint_ref || '')
    if (m) max = Math.max(max, parseInt(m[1], 10))
  }
  return `C${max + 1}`
}

export default function Complaints() {
  // Auditors get their own findings only — never the Complaints log or the full Internal Actions
  // register. Routed to a separate component (not an early-return in OfficeComplaints below) so the
  // hooks in that component are always called unconditionally, per the Rules of Hooks.
  const { isAuditor } = useAuth()
  if (isAuditor) {
    return (
      <div>
        <div className="page-header"><h1>My Findings</h1></div>
        <MyFindings />
      </div>
    )
  }
  return <OfficeComplaints />
}

function OfficeComplaints() {
  const { logAction, canApprove } = useAuth()
  const [view, setView] = useState('complaints')   // complaints | actions
  const [rows, setRows] = useState([])
  const [jobs, setJobs] = useState([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => { load() }, [])

  async function load() {
    setLoading(true)
    const { data, error } = await supabase.from('complaints')
      .select('*').order('complaint_date', { ascending: false, nullsFirst: false })
    if (error) console.warn('complaints load error (run PHASE1_SAC.sql / COMPLAINTS_APPEALS.sql):', error.message)
    setRows(data || [])
    // Optional linkage list — best-effort; an empty list just means the dropdown has no options.
    const { data: jd } = await supabase.from('jobs').select('id, scope, audit_date, clients(name)').order('audit_date', { ascending: false }).limit(500)
    setJobs(jd || [])
    setLoading(false)
  }
  const jobLabel = j => `${j?.clients?.name || 'Client'} — ${j?.scope || 'audit'}${j?.audit_date ? ` (${fmt(j.audit_date)})` : ''}`

  function fmt(d) { return d ? format(parseISO(d), 'd MMM yyyy') : '—' }

  async function save() {
    if (!editing) return
    const f = editing
    if (!(f.description || '').trim()) { toast('Enter a description', 'warning'); return }
    setSaving(true)
    const type = 'Complaint'
    const payload = {
      record_type: type,
      // Reference is auto-assigned on create (C{n}) and never edited afterwards.
      complaint_ref: f.id ? (f.complaint_ref || null) : computeNextRef(rows),
      complaint_date: f.complaint_date || null,
      received_by: f.received_by || null,
      complainant_name: f.complainant_name || null,
      client: f.client || null,
      contact_details: f.contact_details || null,
      job_id: f.job_id || null,
      subject: f.subject || null,
      description: f.description || null,
      within_scope: f.within_scope || null,
      investigation_notes: f.investigation_notes || null,
      outcome: f.outcome || null,
      corrective_required: f.corrective_required || null,
      corrective_action: f.corrective_action || null,
      handled_by: f.handled_by || null,
      reviewed_by: f.reviewed_by || null,
      independence_note: f.independence_note || null,
      status: f.status || 'Open',
      action_taken: f.action_taken || null,
      // auto-stamp closed date when status flips to Closed and none was set
      closed_date: f.status === 'Closed' ? (f.closed_date || new Date().toISOString().split('T')[0]) : (f.closed_date || null),
    }
    const prev = f.id ? rows.find(r => r.id === f.id) : null
    let error
    if (f.id) {
      ({ error } = await supabase.from('complaints').update(payload).eq('id', f.id))
    } else {
      ({ error } = await supabase.from('complaints').insert(payload))
    }
    setSaving(false)
    if (error) { failed("Couldn't save", error.message); return }
    // Audit trail: created / updated / status changed / closed.
    if (logAction) {
      const ref = payload.complaint_ref || f.complainant_name || f.client || (f.description || '').slice(0, 40)
      if (!f.id) logAction(`${type} logged`, 'complaints', null, ref, { record_type: type })
      else {
        logAction(`${type} updated`, 'complaints', f.id, ref, {})
        if (prev && prev.status !== payload.status) {
          logAction(`${type} status → ${payload.status}`, 'complaints', f.id, ref, { from: prev.status || 'Open', to: payload.status })
          if (payload.status === 'Closed') logAction(`${type} closed`, 'complaints', f.id, ref, {})
        }
      }
    }
    setEditing(null)
    load()
  }

  async function remove(row) {
    if (!window.confirm('Delete this complaint?')) return
    const { error } = await supabase.from('complaints').delete().eq('id', row.id)
    if (error) { failed("Couldn't delete", error.message); return }
    load()
  }

  const set = (k, v) => setEditing(e => ({ ...e, [k]: v }))
  const counts = { all: rows.length, open: rows.filter(r => r.status !== 'Closed').length }

  return (
    <div>
      <div className="page-header">
        <h1>{view === 'actions' ? 'Internal Actions' : 'Complaints'}</h1>
        {view === 'complaints' && <button className="primary" onClick={() => setEditing({ ...BLANK, complaint_date: new Date().toISOString().split('T')[0] })}>+ New record</button>}
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid var(--border)' }}>
        {[['complaints', 'Complaints'], ['actions', 'Internal Actions']].map(([v, l]) => (
          <button key={v} onClick={() => setView(v)} style={{ border: 'none', background: 'none', padding: '6px 14px', cursor: 'pointer',
            fontWeight: view === v ? 600 : 400, fontSize: 13, borderRadius: 0,
            borderBottom: view === v ? '2px solid var(--brand)' : '2px solid transparent', color: view === v ? 'var(--brand)' : 'var(--text2)' }}>{l}</button>
        ))}
      </div>

      {view === 'actions' ? <InternalActions /> : <>

      <div style={{ marginBottom: 16 }}>
        <div className="notice notice-info" style={{ fontSize: 13, margin: 0 }}>
          Lifecycle: received → review → response → outcome/decision → corrective action → closed. {counts.open} open of {counts.all} total.
        </div>
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Ref</th><th>Date</th><th>Complainant / client</th><th>Description</th>
                <th>Status</th><th>Action taken</th><th>Closed</th><th></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} style={{ color: 'var(--text3)', textAlign: 'center', padding: 24 }}>Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={8} style={{ color: 'var(--text3)', textAlign: 'center', padding: 24 }}>No records.</td></tr>}
              {rows.map(r => {
                return (
                <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => setEditing({
                  ...r,
                  complaint_date: r.complaint_date || '',
                  closed_date: r.closed_date || '',
                })}>
                  <td style={{ fontWeight: 500 }}>{r.complaint_ref || '—'}</td>
                  <td style={{ fontSize: 13, color: 'var(--text2)' }}>{fmt(r.complaint_date)}</td>
                  <td style={{ fontSize: 13 }}>{r.complainant_name || r.client || '—'}</td>
                  <td style={{ maxWidth: 320, fontSize: 13 }}>{r.description || '—'}</td>
                  <td><span className={`badge ${r.status === 'Closed' ? 'badge-ok' : 'badge-warn'}`}>{r.status || 'Open'}</span></td>
                  <td style={{ maxWidth: 240, fontSize: 13, color: 'var(--text2)' }}>{r.action_taken || '—'}</td>
                  <td style={{ fontSize: 13, color: 'var(--text2)' }}>{fmt(r.closed_date)}</td>
                  <td style={{ whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                    {canApprove ? <button className="sm danger" onClick={() => remove(r)}>Delete</button>
                      : <span style={{ fontSize: 13, color: 'var(--text3)' }}>—</span>}
                  </td>
                </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <div className="modal-overlay" onClick={() => !saving && setEditing(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto', zIndex: 50 }}>
          <div className="card" onClick={e => e.stopPropagation()} style={{ maxWidth: 560, width: '100%' }}>
            <h2 style={{ marginBottom: 14 }}>{editing.id ? 'Edit' : 'New'} complaint</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
              <div className="field"><label>Reference</label><input readOnly value={editing.id ? (editing.complaint_ref || '—') : 'Auto (assigned on save)'} style={{ background: 'var(--bg2)', color: 'var(--text3)' }} /></div>
              <div className="field"><label>Date received</label><input type="date" value={editing.complaint_date || ''} onChange={e => set('complaint_date', e.target.value)} /></div>
              <div className="field"><label>Received by</label><input value={editing.received_by || ''} onChange={e => set('received_by', e.target.value)} /></div>
              <div className="field"><label>Complainant name</label><input value={editing.complainant_name || ''} onChange={e => set('complainant_name', e.target.value)} /></div>
              <div className="field"><label>Organisation / client</label><input value={editing.client || ''} onChange={e => set('client', e.target.value)} /></div>
              <div className="field"><label>Contact details</label><input value={editing.contact_details || ''} onChange={e => set('contact_details', e.target.value)} placeholder="email / phone" /></div>
              <div className="field"><label>Within scope of our services?</label>
                <select value={editing.within_scope || ''} onChange={e => set('within_scope', e.target.value)}>
                  {SCOPE_OPTS.map(x => <option key={x} value={x}>{x || '—'}</option>)}
                </select>
              </div>
              <div className="field" style={{ gridColumn: '1 / -1' }}><label>Related job / audit (optional)</label>
                <select value={editing.job_id || ''} onChange={e => set('job_id', e.target.value)}>
                  <option value="">— none —</option>
                  {jobs.map(j => <option key={j.id} value={j.id}>{jobLabel(j)}</option>)}
                </select>
              </div>
              <div className="field" style={{ gridColumn: '1 / -1' }}><label>Subject / what it relates to</label><input value={editing.subject || ''} onChange={e => set('subject', e.target.value)} placeholder="e.g. audit conduct, report content, certification decision, scheduling" /></div>
              <div className="field" style={{ gridColumn: '1 / -1' }}><label>Description / issue raised *</label><textarea rows={3} value={editing.description || ''} onChange={e => set('description', e.target.value)} /></div>
              <div className="field" style={{ gridColumn: '1 / -1' }}><label>Investigation notes</label><textarea rows={3} value={editing.investigation_notes || ''} onChange={e => set('investigation_notes', e.target.value)} /></div>
              <div className="field" style={{ gridColumn: '1 / -1' }}><label>Outcome / decision</label><textarea rows={2} value={editing.outcome || ''} onChange={e => set('outcome', e.target.value)} /></div>
              <div className="field"><label>Responsible person</label><input value={editing.handled_by || ''} onChange={e => set('handled_by', e.target.value)} /></div>
              <div className="field"><label>Independent reviewer</label><input value={editing.reviewed_by || ''} onChange={e => set('reviewed_by', e.target.value)} /></div>
              <div className="field" style={{ gridColumn: '1 / -1' }}><label>Independence note</label><input value={editing.independence_note || ''} onChange={e => set('independence_note', e.target.value)} placeholder="Where relevant: handler/reviewer independent of the subject of the complaint" /></div>
              <div className="field"><label>Corrective action required?</label>
                <select value={editing.corrective_required || ''} onChange={e => set('corrective_required', e.target.value)}>
                  {YESNO.map(x => <option key={x} value={x}>{x || '—'}</option>)}
                </select>
              </div>
              {editing.corrective_required === 'Yes' && (
                <div className="field" style={{ gridColumn: '1 / -1' }}><label>Corrective action notes</label><textarea rows={2} value={editing.corrective_action || ''} onChange={e => set('corrective_action', e.target.value)} placeholder="Link to an Internal Action if a corrective action is raised" /></div>
              )}
              <div className="field"><label>Status</label>
                <select value={editing.status || 'Open'} onChange={e => set('status', e.target.value)}>
                  {STATUSES.map(x => <option key={x} value={x}>{x}</option>)}
                </select>
              </div>
              <div className="field"><label>Closure date</label><input type="date" value={editing.closed_date || ''} onChange={e => set('closed_date', e.target.value)} /></div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              {/* Office may LOG a new complaint (insert); only QM/Admin may edit/close/delete (per M&P §5). */}
              {(canApprove || !editing.id) && <button className="primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>}
              <button onClick={() => setEditing(null)} disabled={saving}>{(canApprove || !editing.id) ? 'Cancel' : 'Close'}</button>
            </div>
          </div>
        </div>
      )}
      </>}
    </div>
  )
}

// ── AO internal corrective-action register (management-system NCs — NOT client audit findings) ──
// Two of the six sources are "graded": Internal audit and Assessor NC (SAC report) findings carry a
// grading, clause reference, root-cause analysis and a formal corrective-action plan, and can be pushed
// to an individual recipient (auditor or office staff) to fill in themselves before QM/DQM verifies it.
// The other four sources (Complaint, Observation, Management review, Other) stay the original simple form.
const IA_BLANK = {
  source: 'Internal audit', issue: '', action_required: '', owner: '', due_date: '', status: 'Open',
  effectiveness_note: '', closed_date: '',
  grading: '', clause_ref: '', assessment_reference: '',
}
const IA_SOURCES = ['Complaint', 'Internal audit', 'Assessor NC', 'Observation', 'Management review', 'Other']
const IA_STATUSES = ['Open', 'In progress', 'Closed']
const GRADED_SOURCES = ['Internal audit', 'Assessor NC']
const GRADING_OPTS = ['Major NC', 'Minor NC', 'Observation', 'OFI']
const GRADED_STATUS_BADGE = { Open: 'badge-warn', Assigned: 'badge-info', Submitted: 'badge-info', Closed: 'badge-ok' }

function statusBadgeClass(status) {
  return GRADED_STATUS_BADGE[status] || (status === 'Closed' ? 'badge-ok' : status === 'In progress' ? 'badge-info' : 'badge-warn')
}

const AUDITPM_URL = 'https://auditpm.ccis.com.sg'

// Same chrome as the client-facing emails (send-feedback-request / send-audit-reminders / etc.) — teal
// header bar, teal button, firm footer — so internal notification emails don't look like a different,
// uglier system. bodyParagraphs is the message-specific HTML that goes between the greeting and the button.
function brandedEmailHtml(heading, bodyParagraphs) {
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"></head>
<body bgcolor="#f4f5f3" style="margin:0;padding:0;background:#f4f5f3;font-family:Arial,Helvetica,sans-serif;">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#f4f5f3;"><tr><td align="center" style="padding:28px 12px;">
  <table role="presentation" width="600" cellpadding="0" cellspacing="0" style="width:600px;max-width:600px;background:#ffffff;border:1px solid #e3e5e2;">
    <tr><td bgcolor="#006363" style="background:#006363;padding:18px 32px;">
      <div style="font-size:14px;font-weight:700;color:#ffffff;letter-spacing:1.5px;">CCIS SINGAPORE PTE LTD</div>
      <div style="font-size:10px;color:#a9d6d6;letter-spacing:1px;margin-top:3px;">FOR A BRIGHTER BUSINESS TOMORROW</div>
    </td></tr>
    <tr><td style="padding:28px 32px 10px;color:#2b2b2b;">
      <div style="font-size:16px;font-weight:700;color:#0e3b3b;margin:0 0 18px;">${heading}</div>
      <p style="margin:0 0 16px;font-size:15px;color:#1a1a18;">Dear {{staff_name}},</p>
      ${bodyParagraphs}
      <table role="presentation" cellpadding="0" cellspacing="0" style="margin:6px 0 0;"><tr>
        <td bgcolor="#006363" style="background:#006363;border-radius:6px;">
          <a href="${AUDITPM_URL}" style="display:inline-block;padding:11px 30px;font-family:Arial,Helvetica,sans-serif;font-size:15px;font-weight:700;color:#ffffff;text-decoration:none;">Open AuditPM &rarr;</a>
        </td></tr></table>
      <p style="margin:22px 0 4px;font-size:14px;color:#444;line-height:1.6;">Regards,<br>CCIS Singapore Pte Ltd</p>
    </td></tr>
    <tr><td style="padding:16px 32px 22px;border-top:1px solid #ececea;">
      <div style="font-size:11px;color:#9a9a9a;line-height:1.55;">CCIS Singapore Pte Ltd &middot; Reg. No. 199205611D &middot; SAC-Accredited Auditing Organisation<br>16 Jalan Kilang Timor #08-03, Redhill Forum, Singapore 159308</div>
    </td></tr>
  </table>
</td></tr></table>
</body></html>`
}

async function notifyByUserId(userId, subject, heading, bodyParagraphs) {
  // Best-effort, non-blocking — mirrors the pattern already used in AuditorCOIForm.js.
  try { await supabase.functions.invoke('send-internal-email', { body: { user_id: userId, subject, html: brandedEmailHtml(heading, bodyParagraphs) } }) }
  catch (e) { /* non-blocking */ }
}

function InternalActions() {
  const { profile, logAction, canApprove, user } = useAuth()
  const [rows, setRows] = useState([])
  const [recipients, setRecipients] = useState([])   // active AuditPM logins, for the "push to recipient" picker
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(null)
  const [saving, setSaving] = useState(false)
  const [assignTo, setAssignTo] = useState('')
  const [verifyResult, setVerifyResult] = useState('')
  const [verifyComments, setVerifyComments] = useState('')
  const [busy, setBusy] = useState(false)
  const [refFilter, setRefFilter] = useState('')     // filter the register by Assessment / report reference
  useEffect(() => { load() }, [])
  async function load() {
    setLoading(true)
    const { data, error } = await supabase.from('internal_corrective_actions').select('*').order('created_at', { ascending: false })
    if (error) console.warn('internal_corrective_actions load error (run CT17_LAUNCH_PATCH.sql):', error.message)
    setRows(data || [])
    // user_profiles SELECT is self-or-Admin only, so a non-Admin QM can't list recipients directly —
    // list_assignable_users() (FINDINGS_CORRECTIVE_ACTION.sql) is the narrow RPC that does it instead.
    const { data: rc, error: rce } = await supabase.rpc('list_assignable_users')
    if (rce) console.warn('list_assignable_users error (run FINDINGS_CORRECTIVE_ACTION.sql):', rce.message)
    setRecipients(rc || [])
    setLoading(false)
  }
  function fmt(d) { return d ? format(parseISO(d), 'd MMM yyyy') : '—' }
  const set = (k, v) => setEditing(e => ({ ...e, [k]: v }))
  const isGraded = editing && GRADED_SOURCES.includes(editing.source)

  function openEdit(r) {
    setEditing({ ...r, due_date: r.due_date || '', closed_date: r.closed_date || '' })
    setAssignTo(r.assigned_user_id || '')
    setVerifyResult(''); setVerifyComments('')
  }

  async function save() {
    const f = editing
    if (!(f.issue || '').trim()) { toast('Enter the issue', 'warning'); return }
    setSaving(true)
    const graded = GRADED_SOURCES.includes(f.source)
    const payload = {
      source: f.source || null, issue: f.issue || null,
      due_date: f.due_date || null,
      updated_at: new Date().toISOString(),
      // Graded-only fields — harmless (stay null) on the ungraded sources.
      grading: graded ? (f.grading || null) : null,
      clause_ref: graded ? (f.clause_ref || null) : null,
      assessment_reference: graded ? (f.assessment_reference || null) : null,
    }
    if (!graded) {
      // Ungraded sources keep the original simple fields, including a manually-set status and closure note.
      payload.action_required = f.action_required || null
      payload.owner = f.owner || null
      payload.status = f.status || 'Open'
      payload.effectiveness_note = f.effectiveness_note || null
      payload.closed_date = f.status === 'Closed' ? (f.closed_date || new Date().toISOString().split('T')[0]) : (f.closed_date || null)
    } else if (!f.id) {
      // A brand-new graded finding starts life as 'Open' — the workflow buttons (assign/verify) drive status,
      // and closed_date, after that. effectiveness_note/closed_date are deliberately left out of this payload
      // for graded rows so Save never overwrites what verify_corrective_action already set.
      payload.status = 'Open'
    }
    if (graded && f.id && f.assigned_user_id === user?.id) {
      // The signed-in viewer is the assignee themselves (typically an Admin/QM who was also issued the
      // finding) — they may reach for this general Save button instead of the "Submit for review" button
      // inside their response card. Persist their response as a draft either way, so nothing typed is
      // silently lost; only the dedicated Submit button actually flips status to 'Submitted' and notifies.
      payload.correction_taken = f.correction_taken || null
      payload.root_cause = f.root_cause || null
      payload.corrective_action_plan = f.corrective_action_plan || null
    }
    let error, savedId = f.id
    if (f.id) { ({ error } = await supabase.from('internal_corrective_actions').update(payload).eq('id', f.id)) }
    else {
      const { data, error: ie } = await supabase.from('internal_corrective_actions').insert({ ...payload, created_by: profile?.name || 'Staff' }).select().single()
      error = ie; savedId = data?.id
    }
    setSaving(false)
    if (error) { failed("Couldn't save", error.message); return }
    if (logAction) logAction(f.id ? 'Internal action updated' : 'Internal action raised', 'internal_corrective_actions', savedId || null, (f.issue || '').slice(0, 40), {})
    if (graded && !f.id) {
      // Keep the modal open on a brand-new graded finding — straight into "push to recipient" instead of
      // closing and making the QM reopen the row to find it.
      setEditing({ ...f, id: savedId, status: 'Open' })
    } else {
      setEditing(null)
    }
    load()
  }

  async function assign() {
    if (!editing?.id || !assignTo) return
    const rec = recipients.find(r => r.id === assignTo)
    setBusy(true)
    try {
      const { error } = await supabase.from('internal_corrective_actions').update({
        assigned_user_id: assignTo, assigned_name: rec?.name || null, assigned_at: new Date().toISOString(),
        status: 'Assigned', updated_at: new Date().toISOString(),
        // Clear any previous assignee's response/verification — reassigning starts the new person with a
        // clean slate. Without this, reassigning mid-flow would show the new assignee the old assignee's
        // correction/root cause/corrective action pre-filled, and they could submit it as their own.
        correction_taken: null, root_cause: null, corrective_action_plan: null, submitted_at: null,
        verification_result: null, verification_comments: null,
        verified_by_user_id: null, verified_by_name: null, verified_at: null,
      }).eq('id', editing.id)
      if (error) { failed("Couldn't assign it", error.message); return }
      if (logAction) logAction('Corrective action assigned', 'internal_corrective_actions', editing.id, (editing.issue || '').slice(0, 40), { to: rec?.name })
      notifyByUserId(assignTo,
        `Corrective action assigned to you — ${editing.grading || editing.source}${editing.clause_ref ? ' (Clause ' + editing.clause_ref + ')' : ''}`,
        'A finding has been assigned to you',
        `<p style="margin:0 0 16px;font-size:14px;color:#444;line-height:1.7;">A <strong>${(editing.grading || editing.source || 'finding')}</strong>${editing.clause_ref ? ` (Clause ${editing.clause_ref})` : ''} has been assigned to you in AuditPM. Please log in to record the correction taken, root cause and corrective action to prevent recurrence.</p>
        <p style="margin:0 0 18px;font-size:14px;color:#444;line-height:1.7;background:#f4f5f3;border-left:3px solid #006363;padding:10px 14px;"><strong>Issue:</strong> ${(editing.issue || '').replace(/</g, '&lt;')}</p>`)
      setEditing(null); load()
    } catch (e) {
      failed("Couldn't assign it", e?.message || String(e))
    } finally {
      setBusy(false)
    }
  }

  async function verify() {
    if (!editing?.id || !verifyResult) return
    setBusy(true)
    try {
      const { error } = await supabase.rpc('verify_corrective_action', { p_id: editing.id, p_result: verifyResult, p_comments: verifyComments || null })
      if (error) { failed("Couldn't save the verification", error.message); return }
      if (logAction) logAction(`Corrective action ${verifyResult.toLowerCase()}`, 'internal_corrective_actions', editing.id, (editing.issue || '').slice(0, 40), {})
      // Let the assignee know either way — accepted (closed) or rejected (back to them to redo). Without
      // this they'd only find out by reopening AuditPM and noticing the status changed.
      if (editing.assigned_user_id) {
        const accepted = verifyResult === 'Accepted'
        notifyByUserId(editing.assigned_user_id,
          accepted
            ? `Corrective action accepted — ${editing.grading || editing.source}${editing.clause_ref ? ' (Clause ' + editing.clause_ref + ')' : ''}`
            : `Corrective action rejected — action needed — ${editing.grading || editing.source}${editing.clause_ref ? ' (Clause ' + editing.clause_ref + ')' : ''}`,
          accepted ? 'Your corrective action has been accepted' : 'Your corrective action was rejected — please resubmit',
          accepted
            ? `<p style="margin:0 0 16px;font-size:14px;color:#444;line-height:1.7;">Your corrective action response has been reviewed and <strong>accepted</strong> as effective. This finding is now closed.</p>
              <p style="margin:0 0 18px;font-size:14px;color:#444;line-height:1.7;background:#f4f5f3;border-left:3px solid #006363;padding:10px 14px;"><strong>Issue:</strong> ${(editing.issue || '').replace(/</g, '&lt;')}</p>`
            : `<p style="margin:0 0 16px;font-size:14px;color:#444;line-height:1.7;">Your corrective action response has been reviewed and was <strong>not accepted</strong> as effective. Please log in to revise and resubmit.</p>
              <p style="margin:0 0 18px;font-size:14px;color:#444;line-height:1.7;background:#f4f5f3;border-left:3px solid #006363;padding:10px 14px;"><strong>Issue:</strong> ${(editing.issue || '').replace(/</g, '&lt;')}${verifyComments ? `<br><strong>Reviewer comments:</strong> ${verifyComments.replace(/</g, '&lt;')}` : ''}</p>`)
      }
      setEditing(null); load()
    } catch (e) {
      // A thrown (not returned) error — e.g. a network hiccup — used to leave the button stuck on
      // "Saving…" with no visible message. Surface it instead of failing silently.
      failed("Couldn't save the verification", e?.message || String(e))
    } finally {
      setBusy(false)
    }
  }

  // Office-staff self-service: when the signed-in viewer IS the assignee (e.g. an Ops/Sales/Doc Controller
  // recipient — an Auditor recipient uses the separate My Findings page instead), let them fill in and
  // submit their own response right here, the same as My Findings does.
  async function submitMyResponse() {
    if (!editing?.id) return
    if (!(editing.corrective_action_plan || '').trim()) { toast('Enter the corrective action', 'warning'); return }
    setBusy(true)
    try {
      const { error } = await supabase.rpc('submit_corrective_action_response', {
        p_id: editing.id, p_correction_taken: editing.correction_taken || null,
        p_root_cause: editing.root_cause || null, p_corrective_action_plan: editing.corrective_action_plan,
      })
      if (error) { failed("Couldn't submit", error.message); return }
      if (logAction) logAction('Corrective action response submitted', 'internal_corrective_actions', editing.id, (editing.issue || '').slice(0, 40), {})
      try {
        // user_profiles SELECT is self-or-Admin only, so list_qm_users() (FINDINGS_CORRECTIVE_ACTION.sql)
        // is the narrow RPC that lets any signed-in recipient find who to notify. Never notify the assignee
        // about their own submission, even if they also happen to hold a QM/DQM role (self-review is already
        // blocked server-side by verify_corrective_action, but they shouldn't get the "please review" email either).
        const { data: qms } = await supabase.rpc('list_qm_users')
        const assigneeName = editing.assigned_name || profile?.name || 'the assignee'
        for (const qm of (qms || [])) {
          if (qm.id === editing.assigned_user_id) continue
          notifyByUserId(qm.id, `Corrective action submitted for review — ${editing.grading || editing.source}`,
            'A corrective action is awaiting your review',
            `<p style="margin:0 0 16px;font-size:14px;color:#444;line-height:1.7;">A corrective action response has been submitted in AuditPM and is awaiting your verification.</p>
            <p style="margin:0 0 18px;font-size:14px;color:#444;line-height:1.7;background:#f4f5f3;border-left:3px solid #006363;padding:10px 14px;"><strong>Assigned to:</strong> ${assigneeName.replace(/</g, '&lt;')}<br><strong>Issue:</strong> ${(editing.issue || '').replace(/</g, '&lt;')}</p>`)
        }
      } catch (e) { /* non-blocking — email notify best-effort */ }
      setEditing(null); load()
    } catch (e) {
      failed("Couldn't submit", e?.message || String(e))
    } finally {
      setBusy(false)
    }
  }

  async function remove(row) {
    if (!window.confirm('Delete this internal action?')) return
    const { error } = await supabase.from('internal_corrective_actions').delete().eq('id', row.id)
    if (error) { failed("Couldn't delete", error.message); return }
    load()
  }

  // Distinct references for the filter; the register (and the Word export) follow the chosen reference.
  const refs = [...new Set(rows.map(r => r.assessment_reference).filter(Boolean))].sort()
  const filtered = refFilter ? rows.filter(r => r.assessment_reference === refFilter) : rows

  // Export the currently-filtered findings to a Word document — one finding per page, all fields intact.
  // Produced as a Word-openable .doc (HTML) so it needs no extra library or build step.
  function exportWord() {
    if (!filtered.length) { toast('Nothing to export', 'info', { detail: 'No findings match your filter' }); return }
    const esc = s => (s == null ? '' : String(s)).replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c])).replace(/\n/g, '<br/>')
    const rowHtml = (label, val) => `<tr><td style="width:190px;border:1px solid #b7b7b7;padding:7px;background:#f2f2f2;font-weight:bold;vertical-align:top;">${esc(label)}</td><td style="border:1px solid #b7b7b7;padding:7px;vertical-align:top;">${val && String(val).trim() ? esc(val) : '—'}</td></tr>`
    const pages = filtered.map((r, i) => {
      const graded = GRADED_SOURCES.includes(r.source)
      const resp = (r.correction_taken || r.root_cause || r.corrective_action_plan || r.submitted_at)
        ? rowHtml('Correction taken', r.correction_taken) + rowHtml('Root cause', r.root_cause)
          + rowHtml('Corrective action (to prevent recurrence)', r.corrective_action_plan)
          + rowHtml('Response submitted', r.submitted_at ? fmt(r.submitted_at.slice(0, 10)) : '—') : ''
      const verify = r.verification_result
        ? rowHtml('Verification', `${r.verification_result} by ${r.verified_by_name || '—'}${r.verified_at ? ' on ' + fmt(r.verified_at.slice(0, 10)) : ''}`)
          + (r.verification_comments ? rowHtml('Verification comments', r.verification_comments) : '') : ''
      return `<div style="font-family:Calibri,Arial,sans-serif;">
        ${i > 0 ? '<br clear="all" style="mso-special-character:line-break;page-break-before:always">' : ''}
        <h2 style="color:#006363;margin:0 0 4px;">${esc(r.grading || r.source || 'Internal action')}${r.clause_ref ? ' — Clause ' + esc(r.clause_ref) : ''}</h2>
        <div style="font-size:10pt;color:#666;margin:0 0 12px;">${esc(r.assessment_reference || 'No reference')}</div>
        <table style="border-collapse:collapse;width:100%;font-size:11pt;">
          ${rowHtml('Source', r.source)}
          ${graded ? rowHtml('Grading', r.grading) + rowHtml('Clause reference', r.clause_ref) + rowHtml('Assessment / report reference', r.assessment_reference) : ''}
          ${rowHtml('Issue / finding', r.issue)}
          ${!graded ? rowHtml('Action required', r.action_required) : ''}
          ${rowHtml('Issued to', r.assigned_name || r.owner)}
          ${rowHtml('Due date', r.due_date ? fmt(r.due_date) : '—')}
          ${rowHtml('Status', r.status || 'Open')}
          ${rowHtml('Closed date', r.closed_date ? fmt(r.closed_date) : '—')}
          ${resp}
          ${verify}
          ${r.effectiveness_note ? rowHtml('Effectiveness review / closure note', r.effectiveness_note) : ''}
        </table>
      </div>`
    }).join('')
    const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="utf-8"><title>Internal Actions</title></head><body>${pages}</body></html>`
    const blob = new Blob(['﻿', html], { type: 'application/msword' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `Internal_Actions_${(refFilter || 'all').replace(/[^\w.-]+/g, '_')}.doc`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div className="notice notice-info" style={{ fontSize: 13, margin: 0 }}>
          The Auditing Organisation’s own corrective actions (from complaints, internal audits, assessor NCs, observations, management review). Not client audit findings.
        </div>
        {canApprove
          ? <button className="primary" onClick={() => { setEditing({ ...IA_BLANK }); setAssignTo(''); setVerifyResult(''); setVerifyComments('') }}>+ Add action</button>
          : <span style={{ fontSize: 13, color: 'var(--text3)' }}>View only — the Quality Manager manages corrective actions.</span>}
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <label style={{ fontSize: 13, color: 'var(--text3)' }}>Reference</label>
        <select value={refFilter} onChange={e => setRefFilter(e.target.value)} style={{ maxWidth: 320 }}>
          <option value="">All references</option>
          {refs.map(x => <option key={x} value={x}>{x}</option>)}
        </select>
        <button className="sm" onClick={exportWord} disabled={filtered.length === 0}>⬇ Export to Word</button>
        <span style={{ fontSize: 13, color: 'var(--text3)', marginLeft: 'auto' }}>{filtered.length} finding{filtered.length !== 1 ? 's' : ''}{refFilter ? ' · filtered' : ''}</span>
      </div>
      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Source</th><th>Grading</th><th>Issue</th><th>Action / plan</th><th>Recipient / owner</th><th>Due</th><th>Status</th><th>Closed</th><th></th></tr></thead>
            <tbody>
              {loading && <tr><td colSpan={9} style={{ color: 'var(--text3)', textAlign: 'center', padding: 24 }}>Loading…</td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan={9} style={{ color: 'var(--text3)', textAlign: 'center', padding: 24 }}>No internal actions{refFilter ? ' for this reference' : ' recorded'}.</td></tr>}
              {filtered.map(r => {
                const overdue = r.status !== 'Closed' && r.due_date && new Date(r.due_date) < new Date()
                return (
                  <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => openEdit(r)}>
                    <td style={{ fontSize: 13 }}>{r.source || '—'}</td>
                    <td style={{ fontSize: 13 }}>{r.grading || '—'}{r.clause_ref ? ` · ${r.clause_ref}` : ''}</td>
                    <td style={{ maxWidth: 220, fontSize: 13 }}>{r.issue || '—'}</td>
                    <td style={{ maxWidth: 200, fontSize: 13, color: 'var(--text2)' }}>{r.corrective_action_plan || r.action_required || '—'}</td>
                    <td style={{ fontSize: 13 }}>{r.assigned_name || r.owner || '—'}</td>
                    <td style={{ fontSize: 13, color: overdue ? 'var(--danger)' : 'var(--text2)' }}>{fmt(r.due_date)}{overdue ? ' ⚠' : ''}</td>
                    <td><span className={`badge ${statusBadgeClass(r.status)}`}>{r.status || 'Open'}</span></td>
                    <td style={{ fontSize: 13, color: 'var(--text2)' }}>{fmt(r.closed_date)}</td>
                    <td onClick={e => e.stopPropagation()}>{canApprove ? <button className="sm danger" onClick={() => remove(r)}>Delete</button> : <span style={{ fontSize: 13, color: 'var(--text3)' }}>—</span>}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
      {editing && (
        <div className="modal-overlay" onClick={() => !saving && !busy && setEditing(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto', zIndex: 50 }}>
          <div className="card" onClick={e => e.stopPropagation()} style={{ maxWidth: 620, width: '100%' }}>
            <h2 style={{ marginBottom: 14 }}>{editing.id ? 'Edit internal action' : 'Add internal action'}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
              <div className="field"><label>Source</label>
                <select value={editing.source || ''} onChange={e => set('source', e.target.value)}>{IA_SOURCES.map(x => <option key={x}>{x}</option>)}</select></div>
              {isGraded ? (
                <>
                  <div className="field"><label>Grading</label>
                    <select value={editing.grading || ''} onChange={e => set('grading', e.target.value)}>
                      <option value="">— select —</option>{GRADING_OPTS.map(x => <option key={x}>{x}</option>)}
                    </select></div>
                  <div className="field"><label>Clause reference</label><input value={editing.clause_ref || ''} onChange={e => set('clause_ref', e.target.value)} placeholder="e.g. 9.4.4, 7.1.2 in Appendix 1" /></div>
                  <div className="field"><label>Assessment / report reference</label><input value={editing.assessment_reference || ''} onChange={e => set('assessment_reference', e.target.value)} placeholder="groups findings from the same report" /></div>
                </>
              ) : (
                <div className="field"><label>Owner</label><input value={editing.owner || ''} onChange={e => set('owner', e.target.value)} /></div>
              )}
              <div className="field" style={{ gridColumn: '1 / -1' }}><label>Issue / finding *</label><textarea rows={2} value={editing.issue || ''} onChange={e => set('issue', e.target.value)} /></div>
              {!isGraded && (
                <div className="field" style={{ gridColumn: '1 / -1' }}><label>Action required</label><textarea rows={2} value={editing.action_required || ''} onChange={e => set('action_required', e.target.value)} /></div>
              )}
              <div className="field"><label>Due date</label><input type="date" value={editing.due_date || ''} onChange={e => set('due_date', e.target.value)} /></div>
              {!isGraded && (
                <div className="field"><label>Status</label>
                  <select value={editing.status || 'Open'} onChange={e => set('status', e.target.value)}>{IA_STATUSES.map(x => <option key={x}>{x}</option>)}</select></div>
              )}
              {!isGraded && (
                <>
                  <div className="field" style={{ gridColumn: '1 / -1' }}><label>Effectiveness review / closure note</label><textarea rows={2} value={editing.effectiveness_note || ''} onChange={e => set('effectiveness_note', e.target.value)} /></div>
                  <div className="field"><label>Closed date</label><input type="date" value={editing.closed_date || ''} onChange={e => set('closed_date', e.target.value)} /></div>
                </>
              )}
            </div>

            {isGraded && (
              <div style={{ marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Status: <span className={`badge ${statusBadgeClass(editing.status)}`}>{editing.status || 'Open'}</span></div>
                {(editing.assigned_name || editing.owner) && (
                  <div style={{ fontSize: 13, marginBottom: 10 }}><b>Issued to:</b> {editing.assigned_name || editing.owner}{editing.assigned_at ? ` · assigned ${fmt(editing.assigned_at.slice(0, 10))}` : ''}</div>
                )}

                {!editing.id ? (
                  <div style={{ fontSize: 13, color: 'var(--text3)' }}>Save the finding first, then you can push it to a recipient.</div>
                ) : canApprove && editing.status !== 'Closed' && (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13, color: 'var(--text3)' }}>{editing.assigned_name ? `Assigned to ${editing.assigned_name}. Reassign:` : 'Push to recipient:'}</span>
                    <select value={assignTo} onChange={e => setAssignTo(e.target.value)}>
                      <option value="">— choose auditor / staff —</option>
                      {recipients.map(r => <option key={r.id} value={r.id}>{r.name} ({r.role})</option>)}
                    </select>
                    <button className="sm primary" disabled={!assignTo || busy} onClick={assign}>{busy ? 'Working…' : (editing.assigned_name ? 'Reassign' : 'Push to recipient')}</button>
                  </div>
                )}

                {editing.status === 'Assigned' && editing.assigned_user_id === user?.id ? (
                  // The signed-in viewer IS the assignee (office-staff recipient — an Auditor recipient
                  // uses the separate My Findings page) and hasn't submitted yet: let them fill it in here.
                  <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 10, marginBottom: 12 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>This is assigned to you — your response</div>
                    <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 8 }}>Save (below) keeps this as a draft. Click "Submit for review" when you're ready to send it to QM/DQM.</div>
                    <div style={{ display: 'grid', gap: 8 }}>
                      <div className="field"><label>Correction taken (immediate fix)</label><textarea rows={2} value={editing.correction_taken || ''} onChange={e => set('correction_taken', e.target.value)} /></div>
                      <div className="field"><label>Root cause</label><textarea rows={2} value={editing.root_cause || ''} onChange={e => set('root_cause', e.target.value)} /></div>
                      <div className="field"><label>Corrective action (to prevent recurrence) *</label><textarea rows={3} value={editing.corrective_action_plan || ''} onChange={e => set('corrective_action_plan', e.target.value)} /></div>
                    </div>
                    <button className="sm primary" style={{ marginTop: 8 }} disabled={busy} onClick={submitMyResponse}>{busy ? 'Submitting…' : 'Submit for review'}</button>
                  </div>
                ) : (editing.correction_taken || editing.root_cause || editing.corrective_action_plan || editing.submitted_at) && (
                  <div style={{ background: 'var(--bg2)', borderRadius: 8, padding: 10, marginBottom: 12, fontSize: 13 }}>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Recipient's response {editing.submitted_at ? `(submitted ${fmt(editing.submitted_at?.slice(0, 10))})` : '(not yet submitted)'}</div>
                    <div style={{ marginBottom: 4 }}><b>Correction taken:</b> {editing.correction_taken || '—'}</div>
                    <div style={{ marginBottom: 4 }}><b>Root cause:</b> {editing.root_cause || '—'}</div>
                    <div><b>Corrective action (to prevent recurrence):</b> {editing.corrective_action_plan || '—'}</div>
                  </div>
                )}

                {canApprove && editing.status === 'Submitted' && editing.assigned_user_id !== user?.id && (
                  <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 10, marginBottom: 12 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>QM / DQM verification</div>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                      <button className={`sm ${verifyResult === 'Accepted' ? 'primary' : ''}`} onClick={() => setVerifyResult('Accepted')}>Accept — effective</button>
                      <button className={`sm ${verifyResult === 'Rejected' ? 'danger' : ''}`} onClick={() => setVerifyResult('Rejected')}>Reject — not effective</button>
                    </div>
                    <textarea rows={2} placeholder="Comments (optional)" value={verifyComments} onChange={e => setVerifyComments(e.target.value)} style={{ width: '100%', marginBottom: 8 }} />
                    <button className="sm primary" disabled={!verifyResult || busy} onClick={verify}>{busy ? 'Saving…' : 'Save verification'}</button>
                  </div>
                )}

                {editing.verification_result && (
                  <div style={{ fontSize: 13, color: 'var(--text2)' }}>
                    Verification: <b>{editing.verification_result}</b> by {editing.verified_by_name || '—'} {editing.verified_at ? `on ${fmt(editing.verified_at?.slice(0, 10))}` : ''}
                    {editing.verification_comments ? ` — ${editing.verification_comments}` : ''}
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              {canApprove && <button className="primary" onClick={save} disabled={saving || busy}>{saving ? 'Saving…' : 'Save'}</button>}
              <button onClick={() => setEditing(null)} disabled={saving || busy}>{canApprove ? 'Cancel' : 'Close'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ── "My Findings" — the Auditor's own view: only the graded findings assigned to them (SELECT RLS
// already enforces this at the database; the .eq() filter here is just for clarity/perf). ─────────────
function MyFindings() {
  const { user, logAction } = useAuth()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(null)
  const [busy, setBusy] = useState(false)
  useEffect(() => { load() }, [])
  async function load() {
    setLoading(true)
    const { data, error } = await supabase.from('internal_corrective_actions').select('*')
      .eq('assigned_user_id', user?.id).order('assigned_at', { ascending: false })
    if (error) console.warn('My Findings load error:', error.message)
    setRows(data || []); setLoading(false)
  }
  function fmt(d) { return d ? format(parseISO(d), 'd MMM yyyy') : '—' }
  function openEdit(r) { setEditing({ ...r, correction_taken: r.correction_taken || '', root_cause: r.root_cause || '', corrective_action_plan: r.corrective_action_plan || '' }) }

  async function submitResponse() {
    if (!editing) return
    if (!(editing.corrective_action_plan || '').trim()) { toast('Enter the corrective action', 'warning'); return }
    setBusy(true)
    try {
      const { error } = await supabase.rpc('submit_corrective_action_response', {
        p_id: editing.id, p_correction_taken: editing.correction_taken || null,
        p_root_cause: editing.root_cause || null, p_corrective_action_plan: editing.corrective_action_plan,
      })
      if (error) { failed("Couldn't submit", error.message); return }
      if (logAction) logAction('Corrective action response submitted', 'internal_corrective_actions', editing.id, (editing.issue || '').slice(0, 40), {})
      // Best-effort: notify QM/DQM so it isn't only surfaced in-app. Never notify the assignee about their
      // own submission, even if they also happen to hold a QM/DQM role.
      try {
        const { data: qms } = await supabase.rpc('list_qm_users')
        const assigneeName = editing.assigned_name || 'the assignee'
        for (const qm of (qms || [])) {
          if (qm.id === editing.assigned_user_id) continue
          notifyByUserId(qm.id, `Corrective action submitted for review — ${editing.grading || editing.source}`,
            'A corrective action is awaiting your review',
            `<p style="margin:0 0 16px;font-size:14px;color:#444;line-height:1.7;">A corrective action response has been submitted in AuditPM and is awaiting your verification.</p>
            <p style="margin:0 0 18px;font-size:14px;color:#444;line-height:1.7;background:#f4f5f3;border-left:3px solid #006363;padding:10px 14px;"><strong>Assigned to:</strong> ${assigneeName.replace(/</g, '&lt;')}<br><strong>Issue:</strong> ${(editing.issue || '').replace(/</g, '&lt;')}</p>`)
        }
      } catch (e) { /* non-blocking — email notify best-effort */ }
      setEditing(null); load()
    } catch (e) {
      failed("Couldn't submit", e?.message || String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <div className="notice notice-info" style={{ fontSize: 13, margin: '0 0 16px' }}>
        Findings assigned to you. Fill in the correction taken, root cause and corrective action to prevent recurrence, then submit for QM/DQM review.
      </div>
      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Source</th><th>Grading</th><th>Finding</th><th>Assigned</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {loading && <tr><td colSpan={6} style={{ color: 'var(--text3)', textAlign: 'center', padding: 24 }}>Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={6} style={{ color: 'var(--text3)', textAlign: 'center', padding: 24 }}>No findings assigned to you.</td></tr>}
              {rows.map(r => (
                <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => openEdit(r)}>
                  <td style={{ fontSize: 13 }}>{r.source || '—'}</td>
                  <td style={{ fontSize: 13 }}>{r.grading || '—'}{r.clause_ref ? ` · ${r.clause_ref}` : ''}</td>
                  <td style={{ maxWidth: 320, fontSize: 13 }}>{r.issue || '—'}</td>
                  <td style={{ fontSize: 13, color: 'var(--text2)' }}>{fmt(r.assigned_at?.slice(0, 10))}</td>
                  <td><span className={`badge ${statusBadgeClass(r.status)}`}>{r.status || 'Open'}</span></td>
                  <td style={{ fontSize: 13, color: 'var(--text3)' }}>{r.status === 'Assigned' ? 'Fill in →' : 'View →'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {editing && (
        <div className="modal-overlay" onClick={() => !busy && setEditing(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto', zIndex: 50 }}>
          <div className="card" onClick={e => e.stopPropagation()} style={{ maxWidth: 560, width: '100%' }}>
            <h2 style={{ marginBottom: 4 }}>{editing.grading || editing.source}{editing.clause_ref ? ` — Clause ${editing.clause_ref}` : ''}</h2>
            <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 14 }}><span className={`badge ${statusBadgeClass(editing.status)}`}>{editing.status || 'Open'}</span></div>
            <div style={{ fontSize: 13, marginBottom: 14, whiteSpace: 'pre-wrap' }}><b>Finding:</b> {editing.issue || '—'}</div>

            {/* Shown regardless of status — critically, this includes 'Assigned', which is exactly the state
                a REJECTED finding is back in for resubmission. Without this here, an auditor reopening a
                rejected finding would see blank/editable boxes with no indication of why it was sent back. */}
            {editing.verification_result && (
              <div style={{ background: editing.verification_result === 'Rejected' ? '#fdf1e8' : 'var(--bg2)', borderRadius: 8, padding: 10, fontSize: 13, marginBottom: 12 }}>
                <b>QM/DQM review:</b> {editing.verification_result}{editing.verification_comments ? ` — ${editing.verification_comments}` : ''}
                {editing.verified_by_name ? ` (${editing.verified_by_name}${editing.verified_at ? `, ${fmt(editing.verified_at?.slice(0, 10))}` : ''})` : ''}
              </div>
            )}

            {editing.status === 'Assigned' ? (
              <div style={{ display: 'grid', gap: 12 }}>
                <div className="field"><label>Correction taken (immediate fix)</label><textarea rows={2} value={editing.correction_taken} onChange={e => setEditing(f => ({ ...f, correction_taken: e.target.value }))} /></div>
                <div className="field"><label>Root cause</label><textarea rows={2} value={editing.root_cause} onChange={e => setEditing(f => ({ ...f, root_cause: e.target.value }))} /></div>
                <div className="field"><label>Corrective action (to prevent recurrence) *</label><textarea rows={3} value={editing.corrective_action_plan} onChange={e => setEditing(f => ({ ...f, corrective_action_plan: e.target.value }))} /></div>
              </div>
            ) : (
              <div style={{ background: 'var(--bg2)', borderRadius: 8, padding: 10, fontSize: 13 }}>
                <div style={{ marginBottom: 4 }}><b>Correction taken:</b> {editing.correction_taken || '—'}</div>
                <div style={{ marginBottom: 4 }}><b>Root cause:</b> {editing.root_cause || '—'}</div>
                <div><b>Corrective action:</b> {editing.corrective_action_plan || '—'}</div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              {editing.status === 'Assigned' && <button className="primary" onClick={submitResponse} disabled={busy}>{busy ? 'Submitting…' : 'Submit for review'}</button>}
              <button onClick={() => setEditing(null)} disabled={busy}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
